import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DoctorReceipts from './DoctorReceipts';
import { useNavigate } from 'react-router-dom';
import { isAuthTokenValid } from './components/utils';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css"; // Import the CSS for date pickers
import BackupCenter from './components/BackupCenter';
import './DoctorReceiptsDashboard.css'; // Import CSS for additional styling

const DoctorReceiptsDashboard = () => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [salesNumber, setSalesNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [patientInfo, setPatientInfo] = useState(''); // New state for patient info
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState({});
  const [shouldRenderReceipts, setShouldRenderReceipts] = useState(false); // Track rendering
  const [lastBackupDate, setLastBackupDate] = useState('');
  const [searchParams, setSearchParams] = useState(null); // Params to pass to DoctorReceipts

  const navigate = useNavigate();
  const serverURL = process.env.REACT_APP_SERVER_BASE_URL;

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    if (!isAuthTokenValid()) {
      navigate('/authentication?state=doctor-reports');
      return;
    }

    setIsLoading(true);

    axios.get(`${serverURL}/getUser`, {
      headers: {
        authorization: 'Zoho-oauthtoken ' + accessToken,
      },
    })
      .then(response => {
        console.log(response);
        setUser(response.data.response.user);
        setIsAdmin(response.data.response.user.user_role === 'admin' ? true : false);
      })
      .catch(error => {
        console.error('Error fetching user:', error);
        setIsAdmin(false);
      });

    axios.get(`${serverURL}/getDoctors`)
      .then(response => {
        setDoctors(response.data.doctors);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching doctors:', error);
        setIsLoading(false);
      });

    axios.get(`${serverURL}/getBackup`).then(response => {
      setLastBackupDate(response.data.backup_max_validity);
    })
      .catch(error => {
        setLastBackupDate('Error. Last backup is unknown');
      });
  }, [navigate, serverURL]);

  const handleDoctorChange = (event) => {
    setSelectedDoctor(event.target.value);
  };

  const handleDateChange = (setter) => (date) => {
    const formattedDate = date.toISOString().split('T')[0]; // Gets the date part in YYYY-MM-DD format
    setter(formattedDate);
  };

  const handleSalesNumberChange = (event) => {
    setSalesNumber(event.target.value);
  };

  const handleCustomerNameChange = (event) => {
    setCustomerName(event.target.value);
  };

  const handlePatientInfoChange = (event) => {
    setPatientInfo(event.target.value);
  };

  const handleSearch = () => {
    setShouldRenderReceipts(true);
    setSearchParams({
      doctorName: selectedDoctor,
      startDate,
      endDate,
      salesNumber,
      customerName,
      patientInfo // Include patient info in search parameters
    });
  };

  return (
    <div className="doctor-dashboard">
      <div className="header">
        {isAdmin && (
          <BackupCenter />
        )}
      </div>
      {isLoading ? <p>Loading doctors...</p> : (
        <>
          <div>
            <h2>All invoices and receipts for a particular doctor based on date frames</h2>
            <h3>Data is valid up to <span style={{ color: 'red' }}>{lastBackupDate}</span></h3>
          </div>
          <table className="input-table">
            <tbody>
              <tr>
                <td>
                  <label htmlFor="start-date">Start Date:</label>
                  <DatePicker
                    selected={new Date(startDate)}
                    onChange={handleDateChange(setStartDate)}
                    dateFormat="yyyy-MM-dd"
                    id="start-date"
                  />
                </td>
                <td>
                  <label htmlFor="end-date">End Date:</label>
                  <DatePicker
                    selected={new Date(endDate)}
                    onChange={handleDateChange(setEndDate)}
                    dateFormat="yyyy-MM-dd"
                    id="end-date"
                  />
                </td>
                <td>
                  <label htmlFor="doctor-select">Select Doctor:</label>
                  <select
                    id="doctor-select"
                    value={selectedDoctor}
                    onChange={handleDoctorChange}
                  >
                    <option value="">-- Select Doctor --</option>
                    {doctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.name}>
                        {doctor.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <td>
                  <label htmlFor="sales-number">Invoice/Receipt Number:</label>
                  <input
                    type="text"
                    id="sales-number"
                    value={salesNumber}
                    onChange={handleSalesNumberChange}
                    className="input-field" // Add class for styling
                  />
                </td>
                <td>
                  <label htmlFor="customer-name">Customer Name:</label>
                  <input
                    type="text"
                    id="customer-name"
                    value={customerName}
                    onChange={handleCustomerNameChange}
                    className="input-field" // Add class for styling
                  />
                </td>
                <td>
                  <label htmlFor="patient-info">Patient Info:</label>
                  <input
                    type="text"
                    id="patient-info"
                    value={patientInfo}
                    onChange={handlePatientInfoChange}
                    className="input-field" // Add class for styling
                  />
                </td>
              </tr>
              <tr>
                <td colSpan="3" style={{ textAlign: 'center' }}>
                  <button onClick={handleSearch}>Search</button>
                </td>
              </tr>
            </tbody>
          </table>
          {shouldRenderReceipts && searchParams && (
            <DoctorReceipts {...searchParams} />
          )}
        </>
      )}
    </div>
  );
};

export default DoctorReceiptsDashboard;
